import { useEffect, useRef } from 'react'
import api from 'api/api'
import { loadSpaceNotificationCounts } from 'api/loadSpaceNotificationCounts'
import { prefetchNotifications } from 'refactor/hooks/api/prefetchNotifications'
import {
    isNotificationCountStale,
    markSpaceNotificationBadgeEvent,
} from 'refactor/hooks/api/notificationBadgeRefetchPolicy'
import useStore from 'state/knovStore'
import { withChannelAuth } from 'state/channels/channelAuth'
import CableApp from '../actioncable'

export default function useInitNotifications() {
    const activeSpaceId = useStore(state => state.activeSpaceId)
    const userSpaces = useStore(state => state.userSpaces)
    const set = useStore.getState().set

    // Read the current active space from a ref so the long-lived websocket /
    // service-worker subscriptions (mounted once) refresh the count for the
    // space the user is actually viewing, not the one active at mount time.
    const activeSpaceIdRef = useRef(activeSpaceId)
    useEffect(() => {
        activeSpaceIdRef.current = activeSpaceId
    }, [activeSpaceId])

    // Write one space's count into both the active badge (when it's the active
    // space) and the per-space map that feeds the cross-space sidebar dots.
    // A count requested before the space's notifications were last cleared is
    // dropped: it may have counted rows the clear marked seen after it read them.
    const storeSpaceNotificationCount = (spaceId, numNoti, requestedAt) => {
        const isActiveSpace = String(activeSpaceIdRef.current) === String(spaceId)
        if (isNotificationCountStale(spaceId, requestedAt)) return isActiveSpace
        set(state => {
            if (isActiveSpace) state.numNoti = numNoti
            state.spaceNotiCounts = { ...state.spaceNotiCounts, [spaceId]: numNoti }
        })
        return isActiveSpace
    }

    // Full refresh for the active space: update the count, then prime an empty
    // notifications view. A non-zero count is represented by the new-items pill;
    // the mounted notification query must stay untouched until the user refreshes.
    const refreshActiveSpaceNotifications = async spaceId => {
        const requestedAt = Date.now()
        const numNoti = await api.getNotificationsCount(spaceId)
        const isActiveSpace = storeSpaceNotificationCount(spaceId, numNoti, requestedAt)
        // The user may have switched again while this request was in flight.
        // Keep the per-space cache, but do not refresh the newly active space's
        // notification query using a stale space's response.
        if (!isActiveSpace) return
        if (numNoti <= 0) prefetchNotifications()
    }

    const refreshNotificationsCount = async () => {
        if (gon.currentUser) {
            const spaceId = activeSpaceIdRef.current
            const requestedAt = Date.now()
            const numNoti = await api.getNotificationsCount(spaceId)
            storeSpaceNotificationCount(spaceId, numNoti, requestedAt)
        }
    }

    useEffect(() => {
        const user = gon.currentUser
        let channel
        if (user) {
            channel = CableApp.cable.subscriptions.create(
                withChannelAuth({ channel: 'UserChannel', userId: user.id }),
                {
                    received: async data => {
                        if (data.message === 'new notification') {
                            markSpaceNotificationBadgeEvent(activeSpaceIdRef.current)
                            // A live event changes only the count/pill. Even a
                            // no-refetch invalidation can make the mounted query
                            // refresh later through its normal lifecycle.
                            await refreshNotificationsCount()
                        } else if (data.message === 'tweet_quest_completed') {
                            // Close any loading modal first, then show success modal
                            set({ isModalOpen: false })
                            setTimeout(() => {
                                set({
                                    isModalOpen: true,
                                    modalParams: {
                                        type: 'TwitterSuccess',
                                        tweet_url: data.tweet_url,
                                        tweet_count: data.tweet_count,
                                        provider_username: data.provider_username,
                                        quest_id: data.quest_id,
                                        noHeader: true,
                                    },
                                })
                            }, 100)
                        } else if (data.message === 'tweet_quest_failed') {
                            // Close any loading modal first, then show error modal
                            set({ isModalOpen: false })
                            setTimeout(() => {
                                set({
                                    isModalOpen: true,
                                    modalParams: {
                                        type: 'TwitterAuthError',
                                        message:
                                            data.error ||
                                            'Failed to post thread to Twitter. Please try again.',
                                    },
                                })
                            }, 100)
                        }
                    },

                    connected: async () => {
                        // A (re)connect means any broadcasts during the gap
                        // were lost (PWA wake, laptop sleep, network blip).
                        // Reconcile the badge without touching a list the user
                        // may already be reading.
                        markSpaceNotificationBadgeEvent(activeSpaceIdRef.current)
                        await refreshNotificationsCount()
                    },
                },
            )
        }
        return () => channel?.unsubscribe()
    }, [])

    // Populate every space's unread count for the cross-space sidebar dots.
    // Keyed on space MEMBERSHIP only: switching which space is viewed does not
    // change other spaces' counts, so this must not refire on activeSpaceId —
    // doing so re-fetched one request per space on every space switch.
    //
    // Tradeoff: a NON-active space's dot can now drift stale mid-session if new
    // notifications land there while you're elsewhere (previously the per-switch
    // all-spaces refetch masked this). It self-heals on reload, a membership
    // change, or switching into that space. The proper fix is to carry space_id
    // + count in the 'new notification' websocket payload so the exact space's
    // dot updates from the push without any fetch (backend change; see PR).
    useEffect(
        function loadAllSpacesNotificationCounts() {
            if (!userSpaces?.length) return
            const requestedAt = Date.now()
            const applyCount = (spaceId, numNoti) => {
                const isActiveSpace = storeSpaceNotificationCount(spaceId, numNoti, requestedAt)
                if (isActiveSpace && numNoti <= 0) prefetchNotifications()
            }
            void loadSpaceNotificationCounts(
                userSpaces.map(s => s.id),
                api.getNotificationsCounts,
                api.getNotificationsCount,
                applyCount,
            )
        },
        [userSpaces],
    )

    // On a space switch, reflect the already-cached count instantly (no flash),
    // then refresh just the newly-active space — one request per switch instead
    // of one per space. Skipped on the initial mount, where the effect above
    // has already fetched the active space's count.
    const didInitActiveSpaceRef = useRef(false)
    useEffect(
        function syncActiveSpaceNotificationCount() {
            if (!activeSpaceId) return

            const knownCount = useStore.getState().spaceNotiCounts?.[activeSpaceId]
            if (knownCount != null) set({ numNoti: knownCount })

            if (!didInitActiveSpaceRef.current) {
                didInitActiveSpaceRef.current = true
                return
            }

            refreshActiveSpaceNotifications(activeSpaceId)
        },
        [activeSpaceId],
    )

    useEffect(function initServiceWorker() {
        if (gon.currentUser && navigator && navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', async function (e) {
                const { pushReceived } = e.data

                if (pushReceived) {
                    markSpaceNotificationBadgeEvent(activeSpaceIdRef.current)
                    await refreshNotificationsCount()
                }
            })
        }
    }, [])
}
